import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ShieldCheck, HeartHandshake, GraduationCap, ClipboardCheck } from "lucide-react";
import { LineReveal, Reveal, Overline } from "@/components/Reveal";
import { Button, Section } from "@/components/UI";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Slogan } from "@/components/Slogan";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";

function WorkTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/work").then((r) => setItems(r.data.slice(0, 2))).catch(() => {}); }, []);
  if (items.length === 0) return null;
  return (
    <Section className="py-24 lg:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <Reveal><Overline>Our Work</Overline>
            <h2 className="font-serif text-4xl lg:text-5xl text-ccpm-charcoal mt-4 leading-[1.05] max-w-xl">See the restorative difference</h2>
          </Reveal>
        </div>
        <Reveal><Link to="/gallery" className="group inline-flex items-center gap-2 text-ccpm-olive font-semibold" data-testid="home-work-viewall">View the full gallery <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></Link></Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-8" data-testid="home-work-teaser">
        {items.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.1}>
            <Link to="/gallery" className="group block">
              <div className="relative overflow-hidden aspect-[4/3] bg-ccpm-stone">
                {c.before_image
                  ? <BeforeAfter before={c.before_image} after={c.after_image} className="aspect-[4/3]" />
                  : <img src={c.after_image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
              </div>
              <span className="overline text-ccpm-olive mt-4 block">{c.category}</span>
              <h3 className="font-serif text-2xl text-ccpm-charcoal mt-1 group-hover:text-ccpm-olive transition-colors">{c.title}</h3>
            </Link>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}


const ROADMAP = [
  { n: "01", t: "Establishing the Council", d: "Forming governance, founding committees, and an advisory council of clinicians, survivors, and educators.", status: "In progress" },
  { n: "02", t: "Drafting the National Standards", d: "Defining scope of practice, code of ethics, infection prevention, informed consent, and trauma-informed care — open for consultation.", status: "In progress" },
  { n: "03", t: "Building the Certification Framework", d: "Developing curriculum, assessment, and the requirements practitioners will meet to become certified.", status: "Upcoming" },
  { n: "04", t: "Opening Certification & the Registry", d: "Certifying the founding cohort and publishing verified practitioner profiles in the public national registry.", status: "Planned" },
  { n: "05", t: "Advocacy & Recognition", d: "Advancing consistent insurance coverage and integration of restorative micropigmentation within the healthcare journey.", status: "Planned" },
];

const CHAPTERS = [
  { n: "01", t: "Scope of Practice", d: "We define restorative paramedical micropigmentation as a clinical discipline — distinct from cosmetic permanent makeup — with a scope rooted in reconstruction, camouflage, and repair." },
  { n: "02", t: "Infection Prevention & Control", d: "A single national benchmark for sterilization, single-use protocols, and safe practice environments that every certified practitioner is held to." },
  { n: "03", t: "Trauma-Informed Care", d: "Emotional safety is treated as clinical practice. Consent, pacing, and dignity sit at the centre of every restorative treatment." },
  { n: "04", t: "Continuing Competency", d: "Certification is not a one-time event. Practitioners maintain their standing through ongoing education and demonstrated competency." },
];

const SERVICES = [
  { t: "3D Areola Restoration", d: "Realistic nipple-areola complex restoration following mastectomy and breast reconstruction." },
  { t: "Scar Camouflage", d: "Re-pigmentation of surgical and traumatic scars to blend with surrounding skin." },
  { t: "Vitiligo Camouflage", d: "Careful colour restoration for areas affected by loss of pigment." },
  { t: "Burn Scar Restoration", d: "Restorative work in partnership with burn units and reconstructive teams." },
  { t: "Other Restorative Applications", d: "Congenital and post-surgical scar work, and other restorative micropigmentation needs." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative grain overflow-hidden bg-ccpm-ivory">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-36 lg:pt-44 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              <Overline>Canadian Council for Paramedical Micropigmentation</Overline>
            </motion.div>
            <LineReveal
              className="mt-7 font-serif text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] lg:leading-[0.98] tracking-tight text-ccpm-charcoal"
              lines={["Setting Canada's", "National Standard for", <span key="i" className="italic text-ccpm-olive">Restorative</span>, "Paramedical Micropigmentation"]}
            />
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-ccpm-taupe font-light leading-relaxed">
              A national certifying body, standards leader, and educational resource for restorative care — for people affected by cancer, surgery, trauma, congenital conditions, and skin disorders.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-9 inline-flex items-center gap-2.5 px-4 py-2 bg-ccpm-olive/10 border border-ccpm-olive/20">
              <span className="w-2 h-2 rounded-full bg-ccpm-olive animate-pulse" />
              <span className="text-[13px] font-semibold text-ccpm-olive-deep tracking-wide">In development — establishing Canada's national standard</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.98 }}
              className="mt-6 flex flex-wrap gap-4">
              <Button to="/registry/apply" variant="primary" data-testid="hero-register-interest">Register Your Interest <ArrowRight size={16} /></Button>
              <Button to="/standards" variant="outline" data-testid="hero-standards">Explore the Standards</Button>
              <Button to="/about" variant="ghost" data-testid="hero-about">About the Council</Button>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4 }} className="relative">
              <div className="relative overflow-hidden aspect-[3/4] arch-top bg-ccpm-olive grain">
                <motion.div style={{ y: imgY }} className="absolute inset-0 flex flex-col justify-center px-8 lg:px-10 text-ccpm-ivory" data-testid="hero-slogan">
                  <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }} style={{ originX: 0 }} className="w-14 h-px bg-ccpm-ivory/40 mb-6" />
                  <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.05 }} className="overline !text-ccpm-ivory/60">Our promise</motion.span>
                  <p className="mt-4 font-serif text-[1.9rem] sm:text-3xl lg:text-[2.4rem] leading-[1.16]">
                    <motion.span initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }} className="block">Beyond the pigment is a person.</motion.span>
                    <motion.span initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }} className="block italic text-ccpm-olive-soft mt-2">Beyond the technique is a responsibility.</motion.span>
                  </p>
                </motion.div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-ccpm-charcoal text-ccpm-ivory p-6 max-w-[220px] hidden sm:block">
                <p className="font-serif text-3xl">Clinical.</p>
                <p className="font-serif text-3xl text-ccpm-olive-soft italic">Not cosmetic.</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* trust strip */}
        <div className="border-y border-ccpm-border bg-ccpm-stone/40">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-ccpm-border">
            {[["National", "certifying body"], ["Standards", "of practice"], ["Public", "registry & verification"], ["Trauma-informed", "by design"]].map(([a, b], i) => (
              <Reveal key={i} delay={i * 0.08} className="py-8 px-6 text-center lg:text-left">
                <p className="font-serif text-2xl text-ccpm-charcoal">{a}</p>
                <p className="text-xs text-ccpm-taupe mt-1 uppercase tracking-widest">{b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PUBLIC CONSULTATION — front and centre */}
      <div className="bg-ccpm-olive text-ccpm-ivory grain relative overflow-hidden" data-testid="home-consultation">
        <Section className="py-14 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-2 h-2 rounded-full bg-ccpm-ivory animate-pulse" />
                <span className="overline !text-ccpm-ivory/80">Public Consultation — Now Open</span>
              </div>
              <h2 className="font-serif text-3xl lg:text-[2.6rem] leading-[1.08] tracking-tight">
                Exploring the need for a Canadian Association for Paramedical Micropigmentation
              </h2>
              <p className="mt-4 max-w-2xl text-ccpm-ivory/85 font-light leading-relaxed">
                We're inviting practitioners, healthcare professionals, surgeons, nurses, patient advocates, survivors, and the public to help decide whether — and how — a national body should be established. Read the letter and share your perspective.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4 lg:items-end">
              <Button to="/consultation" variant="light" className="w-full sm:w-auto" data-testid="consultation-read-cta">Read &amp; join the consultation <ArrowRight size={16} /></Button>
              <Button to="/consultation#join" variant="outline" className="w-full sm:w-auto !border-ccpm-ivory !text-ccpm-ivory hover:!bg-ccpm-ivory hover:!text-ccpm-olive" data-testid="consultation-comment-cta">Share a comment</Button>
            </div>
          </div>
        </Section>
      </div>

      {/* MARQUEE */}
      <div className="py-8 border-b border-ccpm-border overflow-hidden">
        <Marquee speed={30} gradient={false} autoFill>
          {["Restoration", "Dignity", "Standards", "Competency", "Trust", "Care", "Repair"].map((w, i) => (
            <span key={i} className="font-serif italic text-4xl lg:text-5xl text-ccpm-olive-soft mx-8 select-none">{w} <span className="text-ccpm-border not-italic">·</span></span>
          ))}
        </Marquee>
      </div>

      {/* MISSION */}
      <Section className="py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4"><Reveal><Overline>Our Mission</Overline></Reveal></div>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-serif text-3xl lg:text-[2.7rem] leading-[1.25] text-ccpm-charcoal tracking-tight">
                To establish, uphold, and advance a <span className="text-ccpm-olive italic">national standard</span> for restorative paramedical micropigmentation — protecting the public and elevating the practitioners who serve those healing from cancer, surgery, and trauma.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/about" className="group mt-10 inline-flex items-center gap-2 text-ccpm-olive font-semibold" data-testid="home-about-link">
                Read our vision & governance <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* PILLARS */}
      <div className="bg-ccpm-charcoal text-ccpm-ivory py-24 lg:py-32">
        <Section>
          <Reveal><Overline className="!text-ccpm-olive-soft">What the Council does</Overline></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-10 border border-white/10">
            {[
              { icon: ShieldCheck, t: "Certifies", d: "National certification confirming a practitioner meets our standards of practice." },
              { icon: ClipboardCheck, t: "Sets Standards", d: "Scope, ethics, infection control, and consent — defined once, nationally." },
              { icon: GraduationCap, t: "Educates", d: "Curriculum and continuing competency for a rigorous, evolving field." },
              { icon: HeartHandshake, t: "Advocates", d: "For patients' access to restorative care and public awareness." },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-ccpm-charcoal p-8 lg:p-10">
                <p.icon strokeWidth={1.2} size={34} className="text-ccpm-olive-soft" />
                <h3 className="font-serif text-2xl mt-6">{p.t}</h3>
                <p className="mt-3 text-sm text-white/60 font-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* SPOTLIGHT SERVICES */}
      <Section className="py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal><Overline>Scope of Practice</Overline>
              <h2 className="font-serif text-4xl lg:text-5xl text-ccpm-charcoal tracking-tight mt-5 leading-[1.05]">Restorative applications, not beauty procedures</h2>
              <p className="mt-6 text-ccpm-taupe font-light leading-relaxed">CCPM certification covers restorative work for those affected by medical and congenital conditions.</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 relative overflow-hidden aspect-[4/3]">
                <img src={IMG.skinBack} alt="Natural skin texture" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="divide-y divide-ccpm-border border-t border-ccpm-border">
              {SERVICES.map((s, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="group py-7 flex items-baseline gap-6 hover:pl-2 transition-all duration-300">
                    <span className="font-serif text-lg text-ccpm-olive-soft w-8">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-2xl lg:text-3xl text-ccpm-charcoal group-hover:text-ccpm-olive transition-colors">{s.t}</h3>
                      <p className="mt-2 text-ccpm-taupe font-light">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* MANIFESTO CHAPTERS */}
      <div className="bg-ccpm-stone py-24 lg:py-36">
        <Section>
          <Reveal><Overline>The Standard, in four parts</Overline></Reveal>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-12 mt-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex gap-6">
                  <span className="font-serif text-5xl text-ccpm-olive/40 leading-none">{c.n}</span>
                  <div className="pt-1">
                    <h3 className="font-serif text-2xl text-ccpm-charcoal">{c.t}</h3>
                    <p className="mt-3 text-ccpm-taupe font-light leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}><Button to="/standards" variant="outline" className="mt-14" data-testid="home-standards-cta">Read the National Standards</Button></Reveal>
        </Section>
      </div>

      {/* WORK TEASER (only shows once cases exist) */}
      <WorkTeaser />

      {/* BRAND SLOGAN */}
      <Slogan variant="stone" testId="home-slogan"
        quote="Healing is deeply personal."
        emphasis="Restorative care should be too." />

      {/* ROADMAP */}
      <div className="bg-ccpm-charcoal text-ccpm-ivory py-24 lg:py-36 grain relative overflow-hidden">
        <Section>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline className="!text-ccpm-olive-soft">Our Roadmap</Overline>
                <h2 className="font-serif text-4xl lg:text-5xl text-ccpm-ivory mt-5 leading-[1.05]">The path to launch</h2>
                <p className="mt-6 text-white/60 font-light leading-relaxed">CCPM is being built deliberately and transparently. Here is where we are, and where we're headed on the way to opening certification and the national registry.</p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="relative">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/15" aria-hidden="true" />
                <ul className="space-y-10">
                  {ROADMAP.map((r, i) => {
                    const active = r.status === "In progress";
                    return (
                      <Reveal key={r.n} delay={i * 0.08}>
                        <li className="relative pl-12" data-testid={`roadmap-${r.n}`}>
                          <span className={`absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 ${active ? "bg-ccpm-olive-soft border-ccpm-olive-soft" : "bg-ccpm-charcoal border-white/30"}`}>
                            {active && <span className="absolute inset-0 rounded-full bg-ccpm-olive-soft animate-ping opacity-60" />}
                          </span>
                          <div className="flex flex-wrap items-center gap-4">
                            <span className="font-serif text-3xl text-white/25">{r.n}</span>
                            <h3 className="font-serif text-2xl lg:text-3xl text-ccpm-ivory">{r.t}</h3>
                            <span className={`text-[11px] px-2.5 py-1 font-semibold uppercase tracking-wider ${active ? "bg-ccpm-olive-soft text-ccpm-charcoal" : r.status === "Upcoming" ? "border border-white/30 text-white/70" : "text-white/40"}`}>{r.status}</span>
                          </div>
                          <p className="mt-2 text-white/60 font-light leading-relaxed max-w-xl">{r.d}</p>
                        </li>
                      </Reveal>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* CTA */}
      <Section className="py-24 lg:py-36">
        <div className="relative overflow-hidden bg-ccpm-olive text-ccpm-ivory grain">
          <div className="relative p-10 lg:p-20 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="overline !text-ccpm-ivory/70">Get involved</span>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">Help shape Canada's national standard</h2>
              <p className="mt-5 text-ccpm-ivory/80 font-light max-w-md">CCPM is being established. If you are a practitioner, clinician, or advocate, register your interest and we'll keep you informed as our standards, certification, and registry develop.</p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Button to="/registry/apply" variant="light" data-testid="cta-register">Register your interest <ArrowRight size={16} /></Button>
              <Button to="/news" variant="outline" className="!border-ccpm-ivory !text-ccpm-ivory hover:!bg-ccpm-ivory hover:!text-ccpm-olive" data-testid="cta-follow">Follow our progress</Button>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
